import webify from "../util/webify"
import React, { useEffect, useState } from "react"
import mailSvg from "../../../priv/static/svg/generic/mail.svg"
import phoneSvg from "../../../priv/static/svg/generic/phone.svg"
import closeSvg from "../../../priv/static/svg/generic/x.svg"
import SVG from "react-inlinesvg"
import { FilterState, parseQuery, RecipientType, toQuery } from "../models/FilterState"
import { noCase, snakeCase } from "change-case"

interface RecipientProps {
  recipientType: RecipientType
  recipients: Recipient[]
  passengerPartyIds: number[]
}

type Recipient = {
  id: number
  accountHolderEmails: string
  passengerNames: string
  emergencyContacts: string
  subscribed: boolean
}

function AnnouncementRecipients(props: RecipientProps) {
  let { recipientType } = props
  let parsed = parseQuery(window.location.search)
  let [partyIds, setPartyIds] = useState(props.passengerPartyIds)

  useEffect(() => {
    if (parsed.custom) {
      let newQuery = `?${customListQuery(recipientType, partyIds)}`
      if (window.location.search !== newQuery) {
        history.replaceState({}, "", `${window.location.pathname}?${newQuery}`)
      }
    }
  })

  return (
    <>
      <div className="mb-1 label-component">
        <label className="label-styled">
          {recipientType === "account_holders" && "To Account Holders"}
          {recipientType === "text_account_holders" && "To Account Holders"}
          {recipientType === "passengers" && "To Passengers"}
          {recipientType === "emergency_contacts" && "To Emergency Contacts"}
        </label>
      </div>

      <div className="grid md:flex md:flex-wrap">
        {sortedRecipients(props.recipients, props.recipientType)
          .filter((r) => partyIds.includes(r.id))
          .filter((r) => r.subscribed || recipientType === "emergency_contacts" || recipientType === "passengers")
          .map((recipient) => (
            <div
              key={recipient.id}
              className="inline-grid items-center justify-start grid-flow-col px-2 py-1 mb-2 mr-2 text-gray-800 bg-gray-100 border-gray-200 rounded-sm"
              data-role="recipient">
              {recipientType === "account_holders" ? (
                <SVG src={mailSvg} className="inline w-4 h-4 mr-2 text-gray-500" />
              ) : (
                <SVG src={phoneSvg} className="inline w-4 h-4 mr-2 text-gray-500" />
              )}
              <span className="truncate">
                {recipientType === "account_holders" && recipient.accountHolderEmails}
                {recipientType === "text_account_holders" && recipient.accountHolderEmails}
                {recipientType === "passengers" && recipient.passengerNames}
                {recipientType === "emergency_contacts" && recipient.emergencyContacts}
              </span>
              {parsed.custom !== undefined && (
                <span
                  onClick={() => setPartyIds(partyIds.filter((partyId) => partyId != recipient.id))}
                  data-role={`remove-recipient-${recipient.id}`}>
                  <SVG src={closeSvg} className="inline w-4 h-4 ml-2 text-gray-500" />
                </span>
              )}
            </div>
          ))}
      </div>
      {(recipientType === "account_holders" || recipientType === "text_account_holders") &&
        unsubscribedRecipients(props, partyIds, recipientType).length > 0 &&
        unsubscribedList(props, partyIds, recipientType, parsed, setPartyIds)}
      <div className="button-row">
        <button type="submit" className="mb-3 button sm:mb-0" disabled={partyIds.length === 0}>
          Send Announcement
        </button>
        {parsed.custom === undefined && partyIds.length > 0 && (
          <a
            className="link-button-component"
            data-role="convert-to-individual-recipients"
            href={`${window.location.pathname}?${customListQuery(recipientType, partyIds)}`}>
            Convert to Individual Recipients
          </a>
        )}
      </div>
      <HiddenFilterFields filters={{ ...parsed, passengerPartyIdIn: partyIds }} />
    </>
  )
}

function unsubscribedList(props: any, partyIds: number[], recipientType: any, parsed: any, setPartyIds: any) {
  return (
    <>
      <hr className="my-4" />

      <div className="mb-1 label-component">
        <p className="label-styled">Unsubscribed from announcements:</p>
      </div>

      <div className="grid md:flex md:flex-wrap">
        {unsubscribedRecipients(props, partyIds, recipientType)
          .map((recipient) => recipient.accountHolderEmails)
          .join(", ")}
      </div>
    </>
  )
}

function customListQuery(recipientType: RecipientType, partyIds: number[]): string {
  return toQuery({
    custom: true,
    recipientType: recipientType || "account_holders",
    passengerPartyIdIn: partyIds,
  })
}

function unsubscribedRecipients(props: any, partyIds: number[], recipientType: string) {
  return sortedRecipients(props.recipients, props.recipientType)
    .filter((r) => partyIds.includes(r.id))
    .filter((r) => (recipientType === "account_holders" || recipientType === "text_account_holders") && !r.subscribed)
}

function sortedRecipients(recipients: Recipient[], byType: RecipientType) {
  if (byType === "account_holders")
    return recipients.sort((r1, r2) => r1.accountHolderEmails.localeCompare(r2.accountHolderEmails))
  if (byType === "passengers" || byType === "text_account_holders")
    return recipients.sort((r1, r2) => r1.passengerNames.localeCompare(r2.passengerNames))
  if (byType === "emergency_contacts")
    return recipients
      .filter((r) => r.emergencyContacts !== "")
      .sort((r1, r2) => r1.emergencyContacts.localeCompare(r2.emergencyContacts))

  return recipients
}

interface HiddenFilterFieldsProps {
  filters: FilterState
}

function HiddenFilterFields({ filters }: HiddenFilterFieldsProps) {
  return (
    <>
      {Object.keys(filters).map((key) => {
        let snakeCasedKey = snakeCase(noCase(key))
        return (
          <input type="hidden" key={key} name={`announcement[recipients_filter][${snakeCasedKey}]`} value={filters[key]} />
        )
      })}
    </>
  )
}

webify("announcement-recipients", AnnouncementRecipients)
